import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { linkToPractice, clearError, selectModalOnboarding, selectModalPractice } from '../store/onboardingSlice';
import { Checklist } from './OnboardingViewHelpers';
import './LinkedToPracticeModal.css';

const LinkedToPracticeModal = () => {
  const dispatch = useAppDispatch();
  const modalOnboarding = useAppSelector(state => selectModalOnboarding(state));
  const modalPractice = useAppSelector(state => selectModalPractice(state));
  const loading = useAppSelector(state => state.onboarding.loading);
  const reduxError = useAppSelector(state => state.onboarding.error);

  const [practiceId, setPracticeId] = useState('');
  const [isDso, setIsDso] = useState(false);
  const [success, setSuccess] = useState(false);
  const [localError, setLocalError] = useState(null); // Local error for form validation

  console.log("LinkedToPracticeModal: ", modalOnboarding, modalPractice);

  // Get linked to practice data from the onboarding object
  const isLinkedToPractice = modalOnboarding?.isLinkedToPractice;

  const formatBoolean = (value) => {
    return value ? '✅ Yes' : '❌ No';
  };

  // Early return after all hooks are called
  if (!modalOnboarding) {
    return <div>No onboarding selected</div>;
  }

  const handlePracticeIdChange = (e) => {
    setPracticeId(e.target.value);
    setLocalError(null); // Clear local error when user starts typing
    dispatch(clearError()); // Clear Redux error when user starts typing
  };

  const toggleIsDso = () => {
    setIsDso(!isDso);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!practiceId.trim()) {
      setLocalError('Practice ID is required');
      return;
    }

    setSuccess(false);
    setLocalError(null);
    dispatch(clearError()); // Clear any previous Redux errors

    try {
      await dispatch(linkToPractice({
        id: modalOnboarding.id,
        practiceId: practiceId.trim(),
        isDso: isDso
      })).unwrap();
      
    } catch (error) {
      console.error("Error linking to practice: ", error);
      // Redux error state will be updated by the thunk's rejected case
    }
  };

  // Sample checklist data - in a real app, this would come from props or API
  const stageCheckList = {
    "productType": {
      "workflows": ["voip", "non-voip", "dso"],
      "description": "select the product type (voip, non-voip, dso)"
    }
  };

  return (
    <div className="space-y-4">
      <div>
          
          {/* Current Status */}
          <div className="linked-practice-status-container">
            {modalOnboarding.practiceId == null ?
            (<p className="linked-practice-status-text">
              <span className="linked-practice-status-medium">Current Status:</span> {formatBoolean(isLinkedToPractice)}
            </p>) 
            : 
            (<p className="linked-practice-status-text">
                <span className="linked-practice-status-medium">Current Practice ID:</span> {modalOnboarding.practiceId}
              </p>)
            }
          </div>
            

          {/* Form */}
          <form onSubmit={handleSubmit} className="linked-practice-form">
          
            <div>
              <label htmlFor="practiceId" className="linked-practice-label">
                Practice ID
              </label>
              <input
                id="practiceId"
                type="text"
                value={practiceId}
                onChange={handlePracticeIdChange}
                placeholder="Enter practice ID"
                className="linked-practice-input"
                disabled={loading}
              />
              <label htmlFor="isDso" className="linked-practice-label">
                Is DSO
              </label>
              <input
                id="isDso"
                type="checkbox"
                checked={isDso}
                onChange={toggleIsDso}
                className="linked-practice-input"
                disabled={loading}
              />
            </div>

            {/* Error Message */}
            {(localError || reduxError) && (
              <div className="linked-practice-error">
                {localError || reduxError}
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="linked-practice-success">
                Successfully linked to practice! The page will refresh shortly.
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || !practiceId.trim()}
              className="linked-practice-submit-button"
            >
              {loading ? 'Linking...' : 'Link to Practice'}
            </button>
          </form>

          {/* Instructions */}
          <div className="linked-practice-instructions">
            <p className="linked-practice-instructions-text">
              <strong>Instructions:</strong> Enter the practice ID to link this onboarding to a specific practice. 
              This will update the onboarding status and create a history record.
            </p>
          </div>
          
          {/* Checklist Component */}
          <Checklist 
            stage="isLinkedToPractice" 
            onboarding={modalOnboarding} 
            stateCheckList={stageCheckList} 
          />
        </div>
    </div>
  );
};

export const displayHistoryLinkedToPractice = (onboarding) => {
  // For now, return a simple table row with placeholder data
  // In the future, this would parse actual history data from the onboarding object
  return (
    <tr className="linked-practice-history-row">
      <td className="linked-practice-history-cell">
        {new Date().toLocaleString()}
      </td>
      <td className="linked-practice-history-cell">
        Not set
      </td>
      <td className="linked-practice-history-cell">
        {onboarding.isLinkedToPractice ? '✅' : '❌'}
      </td>
      <td className="linked-practice-history-cell">
        Initial status
      </td>
    </tr>
  );
};

export { LinkedToPracticeModal };
